@if (label) {
  @switch (labelType) {
    @case ('float') {
      <p-floatlabel [variant]="labelVariant">
        <ng-container *ngTemplateOutlet="selectInput" />
        <label [for]="id">{{ label | translate }}</label>
      </p-floatlabel>
    }
    @case ('ifta') {
      <p-iftalabel>
        <ng-container *ngTemplateOutlet="selectInput" />
        <label [for]="id">{{ label | translate }}</label>
      </p-iftalabel>
    }
    @default {
      <label class="label" [for]="id">{{ label | translate }}</label>
      <ng-container *ngTemplateOutlet="selectInput" />
    }
  }
} @else {
  <ng-container *ngTemplateOutlet="selectInput" />
}

@if (hasError) {
  <p-message severity="error" size="small" variant="simple">
    {{ errorI18nKey | translate: errorI18nParams }}
  </p-message>
}


<ng-template #selectInput>
  @switch (type) {
    @case (SelectType.Default) {
      @if (searchInInput) {
        <p-autocomplete
          [id]="id"
          [suggestions]="suggestions"
          [(ngModel)]="value"
          (ngModelChange)="handleChange($event)"
          [ngModelOptions]="{ standalone: true }"
          [optionLabel]="optionLabel"
          [optionValue]="optionValue"
          [optionDisabled]="optionDisabled"
          [placeholder]="placeholder"
          [disabled]="disabled"
          [showClear]="showClear"
          [dropdown]="true"
          [completeOnFocus]="true"
          [forceSelection]="true"
          [appendTo]="appendTo"
          [invalid]="hasError"
          (completeMethod)="handleAutoComplete($event)"
          (onSelect)="handleOptionSelected($event)"
          (onFocus)="focus.emit()"
          (onBlur)="blur.emit(); handleTouched()"
          (onShow)="opened.emit()"
          (onHide)="closed.emit()"
          class="w-100"
        />
      } @else {
      <p-select
        [id]="id"
        [fluid]="fluid"
        [size]="size"
        [options]="options"
        [(ngModel)]="value"
        (ngModelChange)="handleChange($event)"
        [ngModelOptions]="{ standalone: true }"
        [optionLabel]="optionLabel"
        [optionValue]="optionValue"
        [optionDisabled]="optionDisabled"
        [filter]="filter"
        [showClear]="showClear"
        [resetFilterOnHide]="resetFilterOnHide"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [group]="group"
        [optionGroupLabel]="optionGroupLabel"
        [optionGroupChildren]="flatOptionGroupChildren"
        [loading]="loading"
        [appendTo]="appendTo"
        [overlayOptions]="overlayOptions"
        [virtualScroll]="virtualScroll"
        [emptyMessage]="emptyMessage"
        [emptyFilterMessage]="emptyFilterMessage"
        [focusOnHover]="focusOnHover"
        [selectOnFocus]="selectOnFocus"
        [autoOptionFocus]="autoOptionFocus"
        [invalid]="hasError"
        (onFilter)="handleFilter($event)"
        (onFocus)="focus.emit()"
        (onBlur)="blur.emit(); handleTouched()"
        (onShow)="opened.emit()"
        (onHide)="closed.emit()"
        class="w-100">
        @if (isAvatar) {
          <ng-template let-item #item>
            <div class="flex items-center gap-2">
              @if (!item.hideAvatar) {
                <app-avatar [url]="item.avatarUrl" [title]="item[optionLabel]" size="22px" [isPhotoGallery]="false" />
              }
              <span>{{ item[optionLabel] }}</span>
            </div>
          </ng-template>
          <ng-template let-selectedOption #selectedItem>
            <div class="flex items-center gap-2">
              @if (!selectedOption.hideAvatar) {
                <app-avatar [url]="selectedOption.avatarUrl" [title]="selectedOption[optionLabel]" size="22px" [isPhotoGallery]="false" />
              }
              <span>{{ selectedOption[optionLabel] }}</span>
            </div>
          </ng-template>
        } @else if (isImg) {
          <ng-template let-item #item>
            <div class="p-select-item-with-img">
              @if (item?.img) {
                <img [src]="item.img" class="p-select-option-img" alt="" />
              }

              <span>{{ isTranslate ? (item[optionLabel] | translate) : item[optionLabel] }}</span>
            </div>
          </ng-template>
          <ng-template let-selectedOption #selectedItem>
            <div class="p-select-item-with-img">
              @if (selectedOption?.img) {
                <img [src]="selectedOption.img" class="p-select-option-img" alt="" />
              }
              <span>{{ isTranslate ? (selectedOption[optionLabel] | translate) : selectedOption[optionLabel] }}</span>
            </div>
          </ng-template>
        } @else if (isTranslate) {
          <ng-template let-item #item>
            {{ item[optionLabel] | translate }}
          </ng-template>
          <ng-template let-selectedOption #selectedItem>
            {{ selectedOption[optionLabel] | translate }}
          </ng-template>
        }
      </p-select>
      }
    }
    @case (SelectType.Multiple) {
      <p-multiselect
        [id]="id"
        [fluid]="fluid"
        [size]="size"
        [options]="options"
        [(ngModel)]="value"
        (ngModelChange)="handleChange($event)"
        [ngModelOptions]="{ standalone: true }"
        [optionLabel]="optionLabel"
        [optionValue]="optionValue"
        [optionDisabled]="optionDisabled"
        [filter]="filter"
        [selectAll]="null"
        [placeholder]="placeholder"
        [display]="display"
        [showClear]="showClear"
        [showToggleAll]="showToggleAll"
        [maxSelectedLabels]="maxSelectedLabels"
        [selectionLimit]="selectionLimit"
        [disabled]="disabled"
        [loading]="loading"
        [appendTo]="appendTo"
        [overlayOptions]="overlayOptions"
        [virtualScroll]="virtualScroll"
        [emptyMessage]="emptyMessage"
        [emptyFilterMessage]="emptyFilterMessage"
        [focusOnHover]="focusOnHover"
        [selectOnFocus]="selectOnFocus"
        [autoOptionFocus]="autoOptionFocus"
        [invalid]="hasError"
        (onFilter)="handleFilter($event)"
        (onFocus)="focus.emit()"
        (onBlur)="blur.emit(); handleTouched()"
        [panelStyle]="{'max-width': '552px'}"
        class="w-100"
      >
        @if (isAvatar) {
          <ng-template let-item #item>
            <div class="flex min-w-0 items-center gap-2">
              @if (!item.hideAvatar) {
                <app-avatar [url]="item.avatarUrl" [title]="item[optionLabel]" size="22px" [isPhotoGallery]="false" />
              }
              <span class="truncate">{{ item[optionLabel] }}</span>
            </div>
          </ng-template>
        } @else if (isImg) {
          <ng-template let-item #item>
            <div class="p-select-item-with-img">
              @if (item?.img) {
                <img [src]="item.img" class="p-select-option-img" alt="" />
              }

              <span>{{ isTranslate ? (item[optionLabel] | translate) : item[optionLabel] }}</span>
            </div>
          </ng-template>
<!--          TODO: Create custom chip-->
<!--          <ng-template let-selecteditems #selecteditems>-->
<!--            @for (item of selecteditems; track $index) {-->
<!--              <div class="p-select-item-with-img">-->
<!--                @if (item.img) {-->
<!--                  <img [src]="item.img" class="p-select-option-img" alt="" />-->
<!--                }-->
<!--                <div>{{ isTranslate ? (item[optionLabel] | translate) : item[optionLabel] }}</div>-->
<!--              </div>-->
<!--            }-->

<!--          </ng-template>-->
        }
      </p-multiselect>
    }
    @case (SelectType.Cascade) {
      <p-cascadeselect
        [id]="id"
        [(ngModel)]="value"
        (ngModelChange)="handleChange($event)"
        [ngModelOptions]="{ standalone: true }"
        [fluid]="fluid"
        [size]="size"
        [options]="options"
        [optionLabel]="optionLabel"
        [optionGroupLabel]="optionGroupLabel"
        [optionGroupChildren]="optionGroupChildren"
        [optionValue]="optionValue"
        [optionDisabled]="optionDisabled"
        [placeholder]="placeholder"
        [showClear]="showClear"
        [disabled]="disabled"
        [loading]="loading"
        [emptyMessage]="emptyMessage"
        [appendTo]="appendTo"
        [overlayOptions]="overlayOptions"
        [focusOnHover]="focusOnHover"
        [selectOnFocus]="selectOnFocus"
        [autoOptionFocus]="autoOptionFocus"
        [invalid]="hasError"
        (onFocus)="focus.emit()"
        (onBlur)="blur.emit(); handleTouched()"
        (onShow)="opened.emit()"
        (onHide)="closed.emit()"
        class="w-100"
      />
    }
  }
</ng-template>
